import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../Icon';
import { isSameSet } from '../../utils/taskFilters';
import '../../styles/components/FilterPopover.css';

export interface FilterOption {
  value: string;
  label: string;
}

/** A multi-select group of checkboxes (Priority, Assignee, Status…). */
export interface FilterSectionSpec {
  key: string;
  label: string;
  options: FilterOption[];
  values: string[];
  onChange: (values: string[]) => void;
  /** Adds a search box above the options (use for long lists such as people). */
  searchable?: boolean;
  /** What "no filter" looks like for this group. Selections equal to it do not count as active. Defaults to none selected. */
  defaultValues?: string[];
}

/**
 * A single-choice group (radio buttons) for a filter that was a plain
 * <select>: one string of state, one of the options being "no filter".
 */
export interface FilterSelectSpec {
  key: string;
  label: string;
  /** Include the "All …" entry as an option; it is the one whose value equals defaultValue. */
  options: FilterOption[];
  value: string;
  /** The value that means "not filtering". A different value shows a chip and counts. */
  defaultValue: string;
  onChange: (value: string) => void;
  searchable?: boolean;
}

/** A single on/off quick filter shown above the sections (e.g. "Assigned to me"). */
export interface FilterToggleSpec {
  key: string;
  label: string;
  /** Shorter text for the chip, when the label is a full sentence. */
  chipLabel?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

interface FilterPopoverProps {
  sections?: FilterSectionSpec[];
  /** Single-choice groups, shown before the multi-select sections. */
  selects?: FilterSelectSpec[];
  toggles?: FilterToggleSpec[];
  /** Button text. */
  label?: string;
  /** Chips shown beside the button before collapsing to "+N". */
  maxChips?: number;
}

interface ChipSpec {
  id: string;
  text: string;
  removeLabel: string;
  remove: () => void;
}

const NO_SECTIONS: FilterSectionSpec[] = [];
const NO_SELECTS: FilterSelectSpec[] = [];

const FOCUSABLE = 'button:not(:disabled), input:not(:disabled)';

/**
 * The elements Tab can actually land on. A radio group is one tab stop (its
 * checked radio, or the first when none is checked), so the other radios must
 * not count as the "first" or "last" stop of the focus trap.
 */
function tabStops(panel: HTMLElement): HTMLElement[] {
  const inOrder = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE))
    .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
  return inOrder.filter(el => {
    if (!(el instanceof HTMLInputElement) || el.type !== 'radio' || !el.name) return true;
    const group = Array.from(panel.querySelectorAll<HTMLInputElement>('input[type="radio"]')).filter(r => r.name === el.name && !r.disabled);
    const stop = group.find(r => r.checked) ?? group[0];
    return el === stop;
  });
}

/**
 * One "Filters" control for a breadcrumb bar that has outgrown a row of
 * separate selects. A button with a count badge opens a panel holding every
 * multi-select filter together; what is active shows as small removable chips
 * beside the button. Controlled: the page keeps owning each filter's state,
 * this only changes how it is picked.
 *
 * The panel is portalled to <body> because the breadcrumb actions row clips
 * its overflow. Below 768px it becomes a bottom sheet with 44px targets.
 */
export const FilterPopover: React.FC<FilterPopoverProps> = ({
  sections = NO_SECTIONS,
  selects = NO_SELECTS,
  toggles = [],
  label = 'Filters',
  maxChips = 2,
}) => {
  const uid = useId();
  const panelId = `${uid}-panel`;
  const [open, setOpen] = useState(false);
  const [queries, setQueries] = useState<Record<string, string>>({});
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const labelOf = (s: FilterSectionSpec, v: string) => s.options.find(o => o.value === v)?.label ?? v;

  const sectionDirty = (s: FilterSectionSpec) => !isSameSet(s.values, s.defaultValues ?? []);

  const selectDirty = (s: FilterSelectSpec) => s.value !== s.defaultValue;

  const chips = useMemo<ChipSpec[]>(() => {
    const out: ChipSpec[] = [];
    selects.filter(selectDirty).forEach(s => {
      const text = s.options.find(o => o.value === s.value)?.label ?? s.value;
      out.push({
        id: `select:${s.key}`,
        text,
        removeLabel: `Remove filter ${s.label}: ${text}`,
        remove: () => s.onChange(s.defaultValue),
      });
    });
    sections.filter(sectionDirty).forEach(s => {
      s.values.forEach(v => {
        out.push({
          id: `${s.key}:${v}`,
          text: labelOf(s, v),
          removeLabel: `Remove filter ${s.label}: ${labelOf(s, v)}`,
          remove: () => s.onChange(s.values.filter(x => x !== v)),
        });
      });
    });
    toggles.filter(t => t.checked).forEach(t => {
      out.push({
        id: `toggle:${t.key}`,
        text: t.chipLabel ?? t.label,
        removeLabel: `Remove filter ${t.chipLabel ?? t.label}`,
        remove: () => t.onChange(false),
      });
    });
    return out;
    // sectionDirty/labelOf close over props only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, selects, toggles]);

  const count = chips.length;
  // Dirty is what the chips show, so an emptied default section (nothing to
  // show, nothing to clear) never enables Clear all on its own.
  const dirty = count > 0;
  const [isSheet, setIsSheet] = useState(false);

  const clearAll = () => {
    selects.forEach(s => { if (selectDirty(s)) s.onChange(s.defaultValue); });
    sections.forEach(s => { if (sectionDirty(s)) s.onChange([...(s.defaultValues ?? [])]); });
    toggles.forEach(t => { if (t.checked) t.onChange(false); });
  };

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    setQueries({});
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  // Outside click. The whole root (button + chips) counts as inside so a chip
  // click or a second click on the button is handled by its own onClick.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || rootRef.current?.contains(t)) return;
      close(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open, close]);

  // Escape and the Tab trap must work even if focus has strayed outside the
  // panel (e.g. onto the button or a chip).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      if (e.key === 'Escape') { e.preventDefault(); close(true); return; }
      if (e.key === 'Tab' && panelRef.current && !panelRef.current.contains(document.activeElement)) {
        e.preventDefault();
        const items = tabStops(panelRef.current);
        (e.shiftKey ? items[items.length - 1] : items[0])?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  // Place the panel under the button (desktop). Done through CSS custom
  // properties so no inline style prop is needed; the mobile sheet ignores them.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const panel = panelRef.current;
      const trigger = triggerRef.current;
      if (!panel || !trigger) return;
      const rect = trigger.getBoundingClientRect();
      const width = panel.offsetWidth || 320;
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
      panel.style.setProperty('--fp-top', `${rect.bottom + 4}px`);
      panel.style.setProperty('--fp-left', `${left}px`);
    };
    place();
    // The button can disappear under us (the bar collapses into the mobile
    // menu on resize); a panel anchored to nothing should close.
    const onResize = () => {
      if (triggerRef.current && triggerRef.current.getClientRects().length === 0) { setOpen(false); return; }
      place();
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  // Move focus into the panel when it opens.
  useEffect(() => {
    if (!open) return;
    panelRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
  }, [open]);

  const onPanelKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close(true);
      return;
    }
    const panel = panelRef.current;
    if (!panel) return;
    if (e.key === 'Tab') {
      const items = tabStops(panel);
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      return;
    }
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && (e.target as HTMLElement).matches('input[type="checkbox"]')) {
      const boxes = Array.from(panel.querySelectorAll<HTMLInputElement>('input[type="checkbox"]'));
      const i = boxes.indexOf(e.target as HTMLInputElement);
      const next = boxes[i + (e.key === 'ArrowDown' ? 1 : -1)];
      if (next) { e.preventDefault(); next.focus(); }
    }
  };

  const toggleValue = (s: FilterSectionSpec, v: string) => {
    s.onChange(s.values.includes(v) ? s.values.filter(x => x !== v) : [...s.values, v]);
  };

  const openPanel = () => {
    // On a phone this button lives inside the CSS-only "more actions"
    // <details>; fold that menu away so the sheet is not stacked on it.
    setIsSheet(typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 768px)').matches);
    triggerRef.current?.closest('details')?.removeAttribute('open');
    setOpen(true);
  };

  const visibleChips = chips.slice(0, maxChips);
  const hiddenCount = chips.length - visibleChips.length;
  const triggerLabel = count > 0 ? `${label}, ${count} active` : label;

  return (
    <div className="filter-popover" ref={rootRef}>
      <button
        type="button"
        ref={triggerRef}
        className={`header-action-btn filter-popover__trigger${count > 0 ? ' active' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={triggerLabel}
        onClick={() => (open ? close(false) : openPanel())}
      >
        <Icon name="filter" size={14} />
        <span className="filter-popover__trigger-label">{label}</span>
        {count > 0 && <span className="filter-popover__badge" aria-hidden="true">{count}</span>}
      </button>

      {count > 0 && (
        <ul className="filter-popover__chips" aria-label="Active filters">
          {visibleChips.map(c => (
            <li key={c.id} className="filter-popover__chip">
              <span className="filter-popover__chip-text">{c.text}</span>
              <button
                type="button"
                className="filter-popover__chip-x"
                aria-label={c.removeLabel}
                onClick={() => { c.remove(); triggerRef.current?.focus(); }}
              >
                <span aria-hidden="true">×</span>
              </button>
            </li>
          ))}
          {hiddenCount > 0 && (
            <li className="filter-popover__chip filter-popover__chip--more">
              <button
                type="button"
                className="filter-popover__more"
                aria-label={`${hiddenCount} more active filters. Open filters`}
                onClick={openPanel}
              >
                +{hiddenCount}
              </button>
            </li>
          )}
        </ul>
      )}

      {open && createPortal(
        <>
          <div className="filter-popover__backdrop" aria-hidden="true" />
          <div
            ref={panelRef}
            id={panelId}
            className="filter-popover__panel"
            role="dialog"
            aria-label={label}
            aria-modal={isSheet || undefined}
            onKeyDown={onPanelKeyDown}
          >
            <div className="filter-popover__body">
              {toggles.length > 0 && (
                <div className="filter-popover__section" role="group" aria-labelledby={`${uid}-quick`}>
                  <div id={`${uid}-quick`} className="filter-popover__section-title">Quick filters</div>
                  {toggles.map(t => (
                    <label key={t.key} className="filter-popover__option">
                      <input type="checkbox" checked={t.checked} onChange={e => t.onChange(e.target.checked)} />
                      <span>{t.label}</span>
                    </label>
                  ))}
                </div>
              )}
              {selects.map(s => {
                const qk = `select:${s.key}`;
                const q = (queries[qk] ?? '').trim().toLowerCase();
                const shown = q ? s.options.filter(o => o.label.toLowerCase().includes(q)) : s.options;
                const titleId = `${uid}-select-${s.key}`;
                return (
                  <div key={qk} className="filter-popover__section">
                    <div id={titleId} className="filter-popover__section-title">{s.label}</div>
                    {s.searchable && (
                      <input
                        type="search"
                        className="filter-popover__search"
                        placeholder={`Search ${s.label.toLowerCase()}…`}
                        aria-label={`Search ${s.label.toLowerCase()}`}
                        value={queries[qk] ?? ''}
                        onChange={e => setQueries(prev => ({ ...prev, [qk]: e.target.value }))}
                      />
                    )}
                    <div className="filter-popover__options" role="radiogroup" aria-labelledby={titleId}>
                      {shown.map(o => (
                        <label key={o.value} className="filter-popover__option">
                          <input
                            type="radio"
                            name={`${uid}-${s.key}`}
                            checked={s.value === o.value}
                            onChange={() => s.onChange(o.value)}
                          />
                          <span>{o.label}</span>
                        </label>
                      ))}
                      {shown.length === 0 && <div className="filter-popover__empty">No matches</div>}
                    </div>
                  </div>
                );
              })}
              {sections.map(s => {
                const q = (queries[s.key] ?? '').trim().toLowerCase();
                const shown = q ? s.options.filter(o => o.label.toLowerCase().includes(q)) : s.options;
                const titleId = `${uid}-${s.key}`;
                return (
                  <div key={s.key} className="filter-popover__section" role="group" aria-labelledby={titleId}>
                    <div id={titleId} className="filter-popover__section-title">{s.label}</div>
                    {s.searchable && (
                      <input
                        type="search"
                        className="filter-popover__search"
                        placeholder={`Search ${s.label.toLowerCase()}…`}
                        aria-label={`Search ${s.label.toLowerCase()}`}
                        value={queries[s.key] ?? ''}
                        onChange={e => setQueries(prev => ({ ...prev, [s.key]: e.target.value }))}
                      />
                    )}
                    <div className="filter-popover__options">
                      {shown.map(o => (
                        <label key={o.value} className="filter-popover__option">
                          <input
                            type="checkbox"
                            checked={s.values.includes(o.value)}
                            onChange={() => toggleValue(s, o.value)}
                          />
                          <span>{o.label}</span>
                        </label>
                      ))}
                      {shown.length === 0 && <div className="filter-popover__empty">No matches</div>}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="filter-popover__footer">
              <button type="button" className="filter-popover__clear" onClick={clearAll} disabled={!dirty}>Clear all</button>
              <button type="button" className="filter-popover__done" onClick={() => close(true)}>Done</button>
            </div>
          </div>
        </>,
        document.body,
      )}
    </div>
  );
};

export default FilterPopover;
